'use client'

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { Box, Typography } from '@mui/material'
import { Toaster } from 'react-hot-toast'

import { getBrandName } from '@/src/utils/buildUtils'
import { useWallet, useWalletContentData } from '@/src/hooks/wallet'
import { getMobileDeviceType } from '@/src/utils/deviceType'
import useRedirectToHome from '@/src/hooks/navigation/useRedirectToHome'
import useRedirectFromWallet from '@/src/hooks/navigation/useRedirectFromWallet'
import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer/swipeableDrawer'
import { WalletInfoResponse, WalletTransactionsResponse } from '@/src/types/wallet'
import { FAQResponse } from '@/src/api/rtkQuery/walletFAQApi'

import WalletMobileFlyinContent from './WalletMobileFlyinContent'
import WalletMobileCleartripContent from './WalletMobileCleartripContent'
import { WalletPageShimmer } from './walletShimmer'
import { WalletFAQ } from './walletFAQ'
import BottomToolbarWrapper from '../bottomToolbar'
import { isCleartrip } from '@/src/utils/generalUtils/isCleartrip'

interface WalletPageWrapperProps {
    isAppWallet?: boolean
    initialWalletInfo?: WalletInfoResponse | null
    initialTransactions?: WalletTransactionsResponse | null
    initialFAQ?: FAQResponse | null
}

// Dynamically load SignInModal to reduce initial bundle size
const SignInModalWrapper = dynamic(
    () =>
        import(
            /* webpackChunkName: "signin-modal" */ '@/src/components/common/header/variants/flyin/SignInModal/SignInModalWrapper'
        ),
    { ssr: false }
)

const WalletPageWrapper: React.FC<WalletPageWrapperProps> = ({
    isAppWallet = false,
    initialWalletInfo,
    initialTransactions,
    initialFAQ
}) => {
    const router = useRouter()
    const locale = useLocale()
    const t = useTranslations('wallet')
    const tVariants = useTranslations('variants')
    const tCommon = useTranslations('common')
    const aboutWalletLabel = t('aboutWallet', { brandName: tVariants(getBrandName()) })
    const isRtl = locale === 'ar'

    const [modalOpen, setModalOpen] = useState(false)
    const [domainBalanceSheetOpen, setDomainBalanceSheetOpen] = useState(false)
    const [faqDrawerOpen, setFaqDrawerOpen] = useState(false)

    // Use wallet hook with initial SSR data
    const wallet = useWallet({
        initialWalletInfo,
        initialTransactions
    })
    const goToHome = useRedirectToHome()
    const goBackFromWallet = useRedirectFromWallet()

    // Check if device is Android or iOS app (client-side only)
    const [isNativeApp, setIsNativeApp] = useState(false)
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const mobileDeviceType = getMobileDeviceType()
            setIsNativeApp(mobileDeviceType === 'AndroidApp' || mobileDeviceType === 'iPhoneApp')
        }
    }, [])

    // Redirect to sign-in if not logged in
    useEffect(() => {
        if (typeof window !== 'undefined' && !wallet.isLoggedIn) {
            setModalOpen(true)
        }
    }, [wallet.isLoggedIn])

    // Handlers
    const handleModalClose = useCallback(() => {
        setModalOpen(false)
        // If still not logged in, redirect to account page
        if (!wallet.isLoggedIn) {
            router.push(`/${locale}/account`)
        }
    }, [wallet.isLoggedIn, router, locale])

    const handleOpenDomainBalanceSheet = useCallback(() => {
        setDomainBalanceSheetOpen(true)
    }, [])

    const handleCloseDomainBalanceSheet = useCallback(() => {
        setDomainBalanceSheetOpen(false)
    }, [])

    const handleOpenFAQDrawer = useCallback(() => {
        setFaqDrawerOpen(true)
    }, [])

    const handleCloseFAQDrawer = useCallback(() => {
        setFaqDrawerOpen(false)
    }, [])

    const {
        formattedTransactions,
        cleartripFormattedTransactions,
        translatedBalanceError,
        translatedTransactionsError,
        formatAmount,
        cleartripTransactionLabels
    } = useWalletContentData({ wallet, locale })

    const handleGoToHome = useCallback(() => {
        // If appWallet is true, use wallet callback to go back to app
        if (isAppWallet && isCleartrip()) {
            goToHome({ isReloadRequired: false })
        } else if (isAppWallet || isNativeApp) {
            goBackFromWallet({ isReloadRequired: false })
        }  else {
            goToHome({ isReloadRequired: false })
        }
    }, [goToHome, goBackFromWallet, isAppWallet, isNativeApp])

    // Determine if we should show full page shimmer
    // Only show shimmer if we don't have initial data and are loading
    const hasInitialData = !!(initialWalletInfo || initialTransactions)
    const showFullPageShimmer = !hasInitialData && wallet.isLoadingBalance && wallet.isLoadingTransactions

    const baseWalletPageProps = useMemo(() => ({
        displayBalance: wallet.displayBalance,
        displayCurrency: wallet.displayCurrency,
        domainBalances: wallet.domainBalances,
        hasMultipleDomainBalances: wallet.hasMultipleDomainBalances,
        hasMoreTransactions: wallet.hasMoreTransactions,
        activeTab: wallet.activeTab,
        onTabChange: wallet.setActiveTab,
        isLoadingBalance: wallet.isLoadingBalance,
        isFetchingMore: wallet.isFetchingTransactions,
        balanceError: translatedBalanceError,
        transactionsError: translatedTransactionsError,
        onLoadMore: wallet.loadMoreTransactions,
        onRefresh: wallet.refreshWallet,
        onGoToHome: handleGoToHome,
        showBackButton: true,
        isDomainBalanceSheetOpen: domainBalanceSheetOpen,
        onOpenDomainBalanceSheet: handleOpenDomainBalanceSheet,
        onCloseDomainBalanceSheet: handleCloseDomainBalanceSheet,
        isFAQDrawerOpen: faqDrawerOpen,
        onOpenFAQDrawer: handleOpenFAQDrawer,
        onCloseFAQDrawer: handleCloseFAQDrawer,
        formatAmount,
        t,
        tCommon,
        locale,
        aboutWalletLabel
    }), [
        wallet.displayBalance,
        wallet.displayCurrency,
        wallet.domainBalances,
        wallet.hasMultipleDomainBalances,
        wallet.hasMoreTransactions,
        wallet.activeTab,
        wallet.setActiveTab,
        wallet.isLoadingBalance,
        wallet.isFetchingTransactions,
        translatedBalanceError,
        translatedTransactionsError,
        wallet.loadMoreTransactions,
        wallet.refreshWallet,
        handleGoToHome,
        domainBalanceSheetOpen,
        handleOpenDomainBalanceSheet,
        handleCloseDomainBalanceSheet,
        faqDrawerOpen,
        handleOpenFAQDrawer,
        handleCloseFAQDrawer,
        formatAmount,
        t,
        tCommon,
        locale,
        aboutWalletLabel
    ])

    return (
        <Box
            sx={{
                minHeight: '100dvh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default'
            }}
        >
            <Toaster position="bottom-center" reverseOrder={false} gutter={8} />
            {/* Main Content */}
            <Box sx={{ flex: 1 }}>
                {showFullPageShimmer ? (
                    <WalletPageShimmer showBackButton={true} />
                ) : (
                    <>
                        {wallet.isCleartrip ? (
                            <WalletMobileCleartripContent
                                walletPageProps={{
                                    ...baseWalletPageProps,
                                    formattedTransactions: [],
                                    isLoadingTransactions: false
                                }}
                                transactions={cleartripFormattedTransactions}
                                hasMoreTransactions={wallet.hasMoreTransactions}
                                isFetchingMore={wallet.isFetchingTransactions}
                                onLoadMore={wallet.loadMoreTransactions}
                                formatAmount={formatAmount}
                                {...cleartripTransactionLabels}
                                isRtl={isRtl}
                            />
                        ) : (
                            <WalletMobileFlyinContent
                                {...baseWalletPageProps}
                                formattedTransactions={formattedTransactions}
                                isLoadingTransactions={wallet.isLoadingTransactions}
                            />
                        )}
                    </>
                )}

                {/* Hide bottom toolbar when appWallet is true */}
                {!isAppWallet && <BottomToolbarWrapper />}
            </Box>

            {/* Sign In Modal */}
            {modalOpen && <SignInModalWrapper open={modalOpen} onClose={handleModalClose} />}

            {/* Domain Balances Sheet */}
            <SwipeableDrawerWithBackNavigation
                open={domainBalanceSheetOpen}
                onClose={handleCloseDomainBalanceSheet}
                sx={{ height: 'auto', maxHeight: '70dvh' }}
            >
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        {t('domainBalances')}
                    </Typography>

                    {/* Table Header */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 2,
                            mb: 2,
                            pb: 1.5,
                            borderBottom: 2,
                            borderColor: 'divider'
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {t('currency')}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            {t('balance')}
                        </Typography>
                    </Box>

                    {/* Domain Balances List */}
                    <Box sx={{ mb: 3 }}>
                        {wallet.domainBalances.map((db, index) => {
                            const isCurrentDomain = db.currency === wallet.displayCurrency
                            return (
                                <Box
                                    key={db.domain || db.currency}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: 2,
                                        py: 1.5,
                                        borderBottom: index < wallet.domainBalances.length - 1 ? 1 : 0,
                                        borderColor: 'divider'
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: isCurrentDomain ? 700 : 400,
                                            color: isCurrentDomain ? 'text.primary' : 'text.secondary'
                                        }}
                                    >
                                        {db.currency}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: isCurrentDomain ? 700 : 400,
                                            color: isCurrentDomain ? 'text.primary' : 'text.secondary'
                                        }}
                                    >
                                        {formatAmount(db.balance, db.currency)}
                                    </Typography>
                                </Box>
                            )
                        })}
                    </Box>

                    {/* Description Text */}
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            fontSize: '0.875rem',
                            lineHeight: 1.6,
                            mt: 2
                        }}
                    >
                        {t('domainBalancesDescription')}
                    </Typography>
                </Box>
            </SwipeableDrawerWithBackNavigation>

            {/* FAQ Drawer */}
            <SwipeableDrawerWithBackNavigation
                open={faqDrawerOpen}
                onClose={handleCloseFAQDrawer}
                sx={{ height: 'auto', maxHeight: '90dvh' }}
            >
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <WalletFAQ initialFAQ={initialFAQ} />
                </Box>
            </SwipeableDrawerWithBackNavigation>
        </Box>
    )
}

export { WalletPageWrapper }
export default WalletPageWrapper
